import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { csrfFetch } from '../utils/csrfFetch';
import { setTimeoutDuration } from '../utils/sessionTimeout';

/**
 * Custom hook to manage timeout settings
 * @returns {Object} Timeout settings and functions to update them
 */
const useTimeoutSettings = () => {
  const [timeoutHours, setTimeoutHours] = useState(4);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const sessionUser = useSelector(state => state.session.user);
  const isMounted = useRef(true);

  // Cleanup function to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Safe state update functions
  const safeSetTimeoutHours = (hours) => {
    if (isMounted.current) {
      setTimeoutHours(hours);
    }
  };

  const safeSetIsLoading = (loading) => {
    if (isMounted.current) {
      setIsLoading(loading);
    }
  };

  const safeSetError = (err) => {
    if (isMounted.current) {
      setError(err);
    }
  };

  // Fetch current timeout setting
  const fetchTimeoutSetting = async () => {
    // Don't fetch if user is not authenticated or not admin
    if (!sessionUser?.is_admin) {
      safeSetIsLoading(false);
      return;
    }

    safeSetIsLoading(true);
    safeSetError(null);

    try {
      const response = await csrfFetch('/api/settings/timeout');
      if (response.ok) {
        const data = await response.json();
        safeSetTimeoutHours(data.timeout_hours);
        console.log(`Fetched timeout setting: ${data.timeout_hours} hours`);

        // Update the client-side timeout duration
        const timeoutMilliseconds = data.timeout_hours * 60 * 60 * 1000;
        setTimeoutDuration(timeoutMilliseconds);
      } else {
        const errorData = await response.json();
        safeSetError(errorData.error || 'Failed to fetch timeout setting');
      }
    } catch (error) {
      console.error('Error fetching timeout setting:', error);
      safeSetError('An error occurred while fetching timeout setting');
    } finally {
      safeSetIsLoading(false);
    }
  };

  // Update timeout setting
  const updateTimeoutSetting = async (hours) => {
    // Don't update if user is not authenticated or not admin
    if (!sessionUser?.is_admin) {
      return { success: false, message: 'Unauthorized' };
    }

    safeSetIsLoading(true);
    safeSetError(null);

    try {
      const response = await csrfFetch('/api/settings/timeout', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          timeout_hours: hours
        }),
      });

      if (response.ok) {
        const data = await response.json();
        safeSetTimeoutHours(data.timeout_hours);
        console.log(`Updated timeout setting: ${data.timeout_hours} hours`);

        // Update the client-side timeout duration
        const timeoutMilliseconds = hours * 60 * 60 * 1000;
        setTimeoutDuration(timeoutMilliseconds);

        return { success: true, message: 'Timeout setting updated successfully' };
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error || 'Failed to update timeout setting';
        safeSetError(errorMessage);
        return { success: false, message: errorMessage };
      }
    } catch (error) {
      console.error('Error updating timeout setting:', error);
      const errorMessage = 'An error occurred while updating timeout setting';
      safeSetError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      safeSetIsLoading(false);
    }
  };

  // Fetch timeout setting when user becomes authenticated
  useEffect(() => {
    if (sessionUser?.is_admin) {
      fetchTimeoutSetting();
    }
  }, [sessionUser]);

  return {
    timeoutHours,
    isLoading,
    error,
    fetchTimeoutSetting,
    updateTimeoutSetting
  };
};

export default useTimeoutSettings;
